import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Col, Form, Popconfirm, Row } from 'antd';

import { ReactComponent as DeleteIcon } from '../../../../../../../../assets/icons/delete-icon.svg';
import { OVERCHARGE_NOTES_TYPES_OPTIONS } from '../../../../../../../../contants/ClaimsEnums';
import { DEFAULT_DATE_FORMAT } from '../../../../../../../../contants/common';
import { formatDate } from '../../../../../../../../utils/date';
import { convertDateUTCToUserLocal } from '../../../../../../../../utilz/dates';
import { routeFileSelector } from '../../../../../../../../utilz/i18n';
import IconButton from '../../../../../../../components/Buttons/IconButton';
import SvgConverter from '../../../../../../../components/Common/SvgConverter';
import FormSelectBox from '../../../../../../../components/Inputs/FormSelectBox';
import FormTextArea from '../../../../../../../components/Inputs/FormTextArea';
import HiddenFormInput from '../../../../../../../components/Inputs/HiddenFormInput';
import Paper from '../../../../../../../components/Paper/Paper';
import TMSFiledCard from '../../../../../../common/components/TMSFiledCard';
import SaveCancelButtons from '../../../../../../Shipment/components/SaveCancelButtons';

const OverChargeNoteCard = ({
  isLastItem = false,
  shadowVisible = true,
  index = 0,
  noteDetails,
  handleDelete,
  onFieldChange,
  registerForm,
  handleSubmit,
  editMode = false,
  setEditMode,
  handleCancel,
}) => {
  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);
  const [form] = Form.useForm();

  useEffect(() => {
    if (registerForm) {
      const noteKey = noteDetails.sequenceNo;
      registerForm(noteKey, form);
      return () => {
        registerForm(noteKey, null);
      };
    }
  }, [form, noteDetails.sequenceNo, registerForm]);

  useEffect(() => {
    if (noteDetails) {
      form.setFieldsValue({
        ...noteDetails,
        isNew: noteDetails.dirtyFlag === 'N' ? false : noteDetails.isNew,
      });
    }
  }, [form, noteDetails]);

  const isEditContext = Boolean(registerForm);
  const isFormEditable = isEditContext || editMode;

  return (
    <Paper className={`border-muted ${!isLastItem ? 'mb-16' : ''} ${shadowVisible ? '' : 'shadow-none'}`}>
      <Form
        onFinish={(data) => handleSubmit(data, index)}
        onValuesChange={onFieldChange}
        initialValues={noteDetails}
        form={form}
      >
        <div className="border-bottom-muted pt-8 pb-10 px-16">
          <Row justify="space-between align-items-center">
            <Col sm={6}>
              <Row align="middle" gutter={15}>
                <Col className="flex-1">
                  <FormSelectBox
                    options={OVERCHARGE_NOTES_TYPES_OPTIONS}
                    label={t('translation:claims.commentType')}
                    placeholder={t('translation:claims.selectCommentType')}
                    margin="mb-0"
                    name="commentType"
                    editMode={isFormEditable}
                    rules={[
                      {
                        message: 'Please select a comment type',
                        required: true,
                      },
                    ]}
                    shouldUseNativeSelect
                  />
                </Col>
              </Row>
            </Col>
            <Col>
              {isEditContext ? (
                <Popconfirm
                  placement="topRight"
                  title="Are you sure you want to delete this note?"
                  onConfirm={() => handleDelete(index)}
                  okText="Delete"
                  cancelText="Cancel"
                  okType="danger"
                  autoFocus
                  getPopupContainer={(trigger) => trigger.parentElement}
                >
                  <IconButton dataTestId={`delete-${index}`}>
                    <SvgConverter Icon={DeleteIcon} color="iconDanger" />
                  </IconButton>
                </Popconfirm>
              ) : (
                <SaveCancelButtons
                  editMode={editMode}
                  lightMode={false}
                  setEditMode={(val) => setEditMode?.(index, val)}
                  handleRemove={() => handleDelete(index)}
                  isNew={noteDetails.isNew}
                  idSuffix={`product-details-${index + 1}`}
                  isModified
                  onCancel={() => handleCancel?.(index, noteDetails.isNew)}
                />
              )}
            </Col>
          </Row>
        </div>
        <div className="px-15 pt-10">
          <Row gutter={15}>
            <Col sm={8}>
              <FormTextArea
                label={t('translation:claims.comments')}
                placeholder={t('translation:claims.enterComments')}
                editMode={isFormEditable}
                rows={3}
                name="commentText"
                maxLength={255}
              />
              <HiddenFormInput name="commentText" />
            </Col>
            <Col sm={4}>
              <TMSFiledCard containerClass="mt-1" title={t('translation:default.fields.createdOn.title')}>
                {noteDetails?.createdOn
                  ? formatDate(
                      convertDateUTCToUserLocal(noteDetails?.createdOn)?.format('YYYY-MM-DDTHH:mm:ss.SSS') ||
                        noteDetails?.createdOn,
                      DEFAULT_DATE_FORMAT,
                    )
                  : ''}
              </TMSFiledCard>
            </Col>
            <Col sm={4}>
              <TMSFiledCard containerClass="mt-2" title={t('translation:claims.createdBy')}>
                {noteDetails.createdByUser}
              </TMSFiledCard>
            </Col>
            <Col sm={4}>
              <TMSFiledCard containerClass="mt-1" title={t('translation:default.fields.modifiedOn')}>
                {noteDetails?.modifiedOn
                  ? formatDate(
                      convertDateUTCToUserLocal(noteDetails?.modifiedOn)?.format('YYYY-MM-DDTHH:mm:ss.SSS') ||
                        noteDetails?.modifiedOn,
                      DEFAULT_DATE_FORMAT,
                    )
                  : ''}
              </TMSFiledCard>
            </Col>
            <Col sm={4}>
              <TMSFiledCard containerClass="mt-2" title={t('translation:default.fields.modifiedBy')}>
                {noteDetails.modifiedByUser}
              </TMSFiledCard>
            </Col>
          </Row>
        </div>
        <HiddenFormInput name="sequenceNo" />
        <HiddenFormInput name="oldCommentType" />
        <HiddenFormInput name="isNew" />
        <HiddenFormInput name="editMode" />
      </Form>
    </Paper>
  );
};

export default OverChargeNoteCard;
